/* eslint-disable no-underscore-dangle */

import type { IPropertyBag } from '@deity-io/di-data-basics';
import type { Flow } from '@deity-io/di-data-flows';
import { FlowLoader } from '@deity-io/di-data-flows';
import { type IEditorConfig, type IValidationResult, mergeEndpointAndService } from '@deity-io/di-data-flows-editor';
import type { IEntityRepo } from 'components/FlowEditor/helpers/flowHelpers';
import { COMP_ID_TRIGGER } from 'components/FlowEditor/helpers/flowHelpers';
import { META_KEY } from 'contracts/constants';
import _, { cloneDeep, set } from 'lodash';
import { v4 } from 'uuid';
import type {
  FlowActions,
  IAddComponentPayload,
  IChangeComponentGroup,
  IConnectPayload,
  IConnectTailPayload,
  IConnectTriggerPayload,
  ICreateFlowPort,
  IFlowSchema,
  IFlowState,
  IInitFlow,
  IMovePayload,
  IUpdateComponent,
  IUpdateConnectionWaypoints
} from './FlowContext';
import { FlowActionTypes } from './FlowContext';

const cloneFlow = (flowState: IFlowState, clearCache = false): Flow | null => {
  if (clearCache) {
    flowState.flow?.assertPortCache();
  }

  return cloneDeep(flowState.flow);
};

export function FlowReducer(state: IFlowState, action: FlowActions): IFlowState {
  /**
   * @todo only revalidate affected sections
   */
  try {
    switch (action.type) {
      case FlowActionTypes.INIT_FLOW:
        return initFlowState(state, action);
      case FlowActionTypes.SELECT_COMPONENT:
        return {
          ...state,
          selectedComponent: action.payload
        };
      case FlowActionTypes.UPDATE_COMPONENT:
        if (state.locked) {
          return state;
        }

        return updateComponent(state, action.payload);
      case FlowActionTypes.ADD_COMPONENT: {
        return addComponent(state, action.payload);
      }
      case FlowActionTypes.REMOVE_COMPONENT: {
        if (state.locked) {
          return state;
        }

        return removeComponent(state, action.payload);
      }
      case FlowActionTypes.CONNECT: {
        if (state.locked) {
          return state;
        }

        return connectComponents(state, action.payload);
      }
      case FlowActionTypes.DISCONNECT: {
        if (state.locked) {
          return state;
        }

        return disconnectComponent(state, action.payload);
      }
      case FlowActionTypes.CONNECT_TRIGGER: {
        if (state.locked) {
          return state;
        }

        return connectTriggerComponent(state, action.payload);
      }
      case FlowActionTypes.CONNECT_TAIL: {
        if (state.locked) {
          return state;
        }

        return connectTailComponent(state, action.payload);
      }
      case FlowActionTypes.TRANSLATE_COMPONENT:
        if (state.locked) {
          return state;
        }

        return moveComponent(state, action.payload);
      case FlowActionTypes.TOGGLE_PANEL:
        return {
          ...state,
          activePanel: action.payload
        };
      case FlowActionTypes.UPDATE_FLOW_SETTING:
        if (state.locked) {
          return state;
        }

        return updateFlowSetting(state, action.payload.property, action.payload.value);

      case FlowActionTypes.CLEAR:
        return {
          activePanel: null,
          selectedComponent: null,
          flowSchema: null,
          flow: null,
          structureHash: ''
        };
      case FlowActionTypes.CLEAR_ERROR: {
        return { ...state, reducerErrors: undefined, structureHash: v4() };
      }
      case FlowActionTypes.CHANGE_GROUP:
        if (state.locked) {
          return state;
        }

        return changeComponentGroup(state, action.payload);
      case FlowActionTypes.TOGGLE_COLLAPSED:
        if (state.locked) {
          return state;
        }

        return toggleCollapsed(state, action.payload.componentId, action.payload.collapsed);
      case FlowActionTypes.CREATE_FLOW_PORT:
        if (state.locked) {
          return state;
        }

        return createFlowPort(state, action.payload);
      case FlowActionTypes.UPDATE_CONNECTION_WAYPOINTS:
        if (state.locked) {
          return state;
        }

        return updateConnectionWaypoints(state, action.payload);
      case FlowActionTypes.REMOVE_SELECTED:
        if (state.locked || !state.selectedComponent) {
          return state;
        }

        return removeSelectedComponent(state);
      default:
        return state;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error in the FlowReducer. with action "${action.type}"`, err);

    return { ...state, reducerErrors: [`${err}`] };
  }
}

const addComponent = (state: IFlowState, payload: IAddComponentPayload): IFlowState => {
  const { componentId, componentConfig, groupComponentId } = payload;
  // const flow = FlowLoader.load(state.flow!.key!, cloneDeep(state.flowSchema!)) as Flow;
  const flow = cloneFlow(state);
  const comp = flow!.addComponent(componentId, componentConfig, groupComponentId);
  if ((comp as IValidationResult).valid === false) {
    // eslint-disable-next-line no-console
    console.warn(comp);
  }
  const json = flow!.toJSON();

  return {
    ...state,
    flow,
    flowSchema: json as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const updateComponent = (state: IFlowState, payload: IUpdateComponent['payload']): IFlowState => {
  const flow = cloneFlow(state /* , payload.regenerateHash */);
  if (flow) {
    const component = flow.getComponent(payload.compId);
    if (Array.isArray(component) || !component) {
      return state;
    }

    const { config } = component;
    const oldValue = _.get(config, payload.key);
    if ((payload.regenerateHash || !_.isEqual(oldValue, payload.val)) && component) {
      const newConfig = cloneDeep(component.config);
      if (payload.val === undefined) {
        // do a delete
        const parent = payload.key.length > 1 ? _.get(newConfig, payload.key.slice(0, -1)) : newConfig;
        const childKey = [...payload.key].pop() as string;
        if (Array.isArray(parent)) {
          // think this ia a bug? Nothing is done with newPArent
          const newParent = parent.splice(parseInt(childKey, 10), 1);
        } else {
          delete parent[childKey];
        }
      } else {
        _.set(newConfig, payload.key, payload.val);
      }
      (component as any)._config = newConfig;
      // Don't want to do validation of full flow on every update. But i Don't have onBlur on all components
      const newState = { ...state, flow, flowSchema: flow.toJSON() as IFlowSchema };
      if (payload.regenerateHash === true) {
        newState.flowSummary = flow.validate(flow);
        newState.structureHash = v4();
      }

      return newState;
    }
  }

  return state;
};

export const removeComponent = (state: IFlowState, componentId: string): IFlowState => {
  const flow = cloneFlow(state);
  const flowPorts = flow?.getFlowPorts() || {};

  let changed = false;
  if (componentId in flowPorts) {
    flow!.removeFlowPort(componentId);
    changed = true;
  } else {
    changed = flow!.removeComponent(componentId);
  }
  if (!changed) {
    return state;
  }

  return {
    ...state,
    flow,
    selectedComponent: null,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const connectTriggerComponent = (state: IFlowState, payload: IConnectTriggerPayload) => {
  const flow = cloneFlow(state);
  if (!flow) {
    return state;
  }

  const to = flow.getComponent(payload.to.compId)!;
  const [success, messages] = flow.connectTrigger(to, false);

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const connectTailComponent = (state: IFlowState, payload: IConnectTailPayload) => {
  const flow = cloneFlow(state);
  if (!flow) {
    return state;
  }

  const comp = flow.getComponent(payload.from.compId)!;
  const [success, messages] = flow.connectTail(comp, payload.from.portKey, payload.tail, false);
  if (!success) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to connect tail component. Reason :${messages.join('. ')}`);
  }

  return {
    ...state,
    flow,
    flowSchema: flow.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow.validate(flow)
  };
};

const connectComponents = (state: IFlowState, payload: IConnectPayload) => {
  const { from, to } = payload;
  const flow = cloneFlow(state);
  if (from.compId === COMP_ID_TRIGGER) {
    flow!.trigger = payload.to.compId;
  } else {
    const fromComp = flow!.getComponent(from.compId)!;
    const toComp = flow!.getComponent(to.compId)!;
    if (toComp) {
      // check if its a tail
    }
    const [connected, messages] = flow!.connect(fromComp, from.portKey, toComp, to.portKey);
    if (!connected) {
      /**
       * @todo, give error back to front. Now the connection is still drawn in graph
       */
      // eslint-disable-next-line no-console
      console.warn('Unable to connect', messages);
    }
  }

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const disconnectComponent = (state: IFlowState, payload: IConnectPayload) => {
  /**
   * @todo, Remove the config.editor.waypoints of this port
   */
  const { from, to } = payload;
  const flow = cloneFlow(state);
  if (!flow) {
    return state;
  }
  let changed = false;
  const fromComp = flow.getComponent(from.compId)!;
  if (!fromComp && to.compId === flow.trigger && flow.trigger !== undefined) {
    changed = true;
    flow.trigger = undefined;
  } else {
    const toComp = flow.getComponent(to.compId)!;
    if (!toComp) {
      const ports = flow.getFlowPorts();
      if (to.compId in ports) {
        delete fromComp?.config[from.portKey];
        changed = true;
      }
    } else {
      const [disConnected, messages] = flow.disconnect(fromComp, from.portKey, toComp, to.portKey);
      if (!disConnected) {
        // eslint-disable-next-line no-console
        console.warn('Unable to disconnect component ', messages);
      } else {
        changed = true;
      }
    }
  }
  if (!changed) {
    return state;
  }

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const setEditorConfig = (config: IPropertyBag, options: Partial<IEditorConfig>) => {
  const settings = config.editor || {};
  config.editor = { ...settings, ...options };
};

/**
 * Gets a config we can directly write to
 *  if compIdOrPath is an path array we expect it tho be nested in the flow config
 *  if its a string it's the config of a component
 *
 */
const getConfig = (flow: Flow, compIdOrPath: string | Array<string>) => {
  const config = Array.isArray(compIdOrPath) ? flow!.config : flow!.getComponent(compIdOrPath)?.config;
  if (config) {
    if (!config.editor) {
      config.editor = {};
    }
    if (Array.isArray(compIdOrPath)) {
      let fetched = _.get(config, compIdOrPath);
      if (fetched === undefined) {
        fetched = {};
        _.set(config, compIdOrPath, fetched);
      }

      return fetched;
    }

    return config.editor;
  }
  // eslint-disable-next-line no-console
  console.warn(`component ${compIdOrPath} not found`);

  return {};
};

const moveComponent = (state: IFlowState, payload: IMovePayload): IFlowState => {
  const { compId, point, size } = payload;
  const flow = cloneFlow(state);
  const config = getConfig(flow!, payload.flowSettingPath ? payload.flowSettingPath : compId);

  config.location = { ...point };
  if (size) {
    config.size = { ...size };
  }

  const flowSchema = flow?.toJSON() as IFlowSchema;

  return { ...state, flow, flowSchema, structureHash: v4() };
};

const updateFlowSetting = (state: IFlowState, key: string | Array<string>, value: string | undefined) => {
  const flow = cloneFlow(state);
  const path = Array.isArray(key) ? key : [key];
  if (value === undefined || value === '') {
    _.unset(flow!.config, path);
    // delete flow!.config[key];
  } else {
    _.set(flow!.config, path, value);
    // flow!.config[key] = value;
  }

  return { ...state, flow, flowSchema: flow?.toJSON() as IFlowSchema, flowSummary: flow?.validate(flow) };
};

const changeComponentGroup = (state: IFlowState, payload: IChangeComponentGroup['payload']) => {
  const flow = cloneFlow(state);
  if (!flow?.changeComponentParent(payload.componentId, payload.targetGroupId)) {
    // eslint-disable-next-line no-console
    console.warn('Unable to change component Parent', payload);
  }

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const toggleCollapsed = (state: IFlowState, compId: string, collapsed: boolean) => {
  const flow = cloneFlow(state);
  const config = compId === '@trigger' ? flow!.config : flow!.getComponent(compId)?.config;
  if (config) {
    setEditorConfig(config, { collapsed });
  }
  const flowSchema = flow?.toJSON() as IFlowSchema;

  return { ...state, flow, structureHash: v4(), flowSchema };
};

const createFlowPort = (state: IFlowState, payload: ICreateFlowPort['payload']) => {
  const flow = cloneFlow(state);
  if (!flow) {
    // eslint-disable-next-line no-console
    console.warn('Unable to load flow.');

    return state;
  }
  if (!('outputs' in flow.config)) {
    flow.config.outputs = {};
  }

  flow.config.outputs[payload.key] = { ...(payload.settings || {}), ...{ editor: { location: payload.position } } };

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema,
    structureHash: v4(),
    flowSummary: flow?.validate(flow)
  };
};

const updateConnectionWaypoints = (state: IFlowState, payload: IUpdateConnectionWaypoints['payload']): IFlowState => {
  const flow = cloneFlow(state);
  if (!flow) {
    // eslint-disable-next-line no-console
    console.warn('Unable to load flow.');

    return state;
  }
  const waypoints = payload.waypoints.map(point => ({ x: point.x, y: point.y }));

  if ('componentId' in payload) {
    const component = flow.getComponent(payload.componentId);
    if (!component) {
      // eslint-disable-next-line no-console
      console.warn(`Component Not Found ${payload.componentId}`);

      return state;
    }

    const portConfig: IPropertyBag = component.config.editor?.ports || {};
    if (waypoints.length) {
      portConfig[payload.port] = {
        waypoints
      };
    } else if (payload.port in portConfig) {
      delete portConfig[payload.port].waypoints;
    }
    setEditorConfig(component.config, { ports: portConfig });
  } else {
    const config = getConfig(flow!, payload.flowSettingPath);
    if (config) {
      if (waypoints.length) {
        config.waypoints = waypoints;
      } else {
        delete config.waypoints;
      }
    }
  }

  return {
    ...state,
    flow,
    flowSchema: flow!.toJSON() as IFlowSchema
  };
};

/**
 * Remove the component or the connection of the current selection
 * @param state : IFlowState
 * @returns IFlowState
 *
 * @todo, we are not checking if it's allowed in editor.menuFactory there are additional checks
 * edge : (isEditable && cell.parent?.isLocked !== true && cell.parent?.isContentLocked !== true)
 * component : (cell.disableDelete !== true && cell.isLocked !== true && cell.parent?.isContentLocked !== true)
 */
const removeSelectedComponent = (state: IFlowState): IFlowState => {
  if (state.selectedComponent) {
    const selected = state.selectedComponent;
    if ('targetComponentId' in selected) {
      // its a connection
      return disconnectComponent(state, {
        from: {
          compId: selected.componentId,
          portKey: selected.port
        },
        to: {
          compId: selected.targetComponentId,
          portKey: selected.targetPort
        }
      });
    }
    if ('componentId' in selected) {
      // its a component, or just the port i selected (still delete component)
      return removeComponent(state, selected.componentId);
    }
  }

  return state;
};

const initFlowState = (state: IFlowState, action: IInitFlow): IFlowState => {
  /**
   * Append extra data to item (config), to use in editor
   * @param schema JSON schema data
   * @param item GQL EntityType
   */
  const appendEditorData = (schema: any, item: any) => {
    if (META_KEY in schema) {
      set(schema, [META_KEY, 'editor', 'name'], item.name);
      set(schema, [META_KEY, 'editor', 'uuid'], item.uuid);
    } else {
      set(schema, ['editor', 'name'], item.name);
      set(schema, ['editor', 'uuid'], item.uuid);
    }
  };

  /**
   * Create a deep copy schema, append editor specific data and index output on uuid
   */
  const array2Record = (input: Array<any>, keyFieldName = 'uuid'): IPropertyBag => {
    const output = {};
    input.forEach(item => {
      const schema = cloneDeep(item.schema);
      appendEditorData(schema, item);
      output[item[keyFieldName]] = schema;
    });

    return output;
  };

  /**
   * Create a deep copy schema, append editor specific data and index output on uuid
   * But also Merge the service and the endpoint definitions
   */
  const endpointArray2Record = (input: Array<any>, keyFieldName = 'uuid'): IPropertyBag => {
    const output = {};
    input.forEach(item => {
      const schema = mergeEndpointAndService(item?.schema, item?.WebService?.schema);
      appendEditorData(schema, item);
      output[item[keyFieldName]] = schema;
    });

    return output;
  };

  // if items not an array, then they should already be a record in the correct format
  const entityRepo: IEntityRepo = {
    mappings: Array.isArray(action.payload.mappings) ? array2Record(action.payload.mappings) : action.payload.mappings,
    models: Array.isArray(action.payload.models) ? array2Record(action.payload.models) : action.payload.models,
    endpoints: Array.isArray(action.payload.endpoints)
      ? endpointArray2Record(action.payload.endpoints)
      : action.payload.endpoints,
    flows: { ...(Array.isArray(action.payload.flows) ? array2Record(action.payload.flows) : action.payload.flows) }
  };

  // remove the this flow from callbacks. prevents recursion
  delete entityRepo.flows[action.payload.uuid];

  const flow = FlowLoader.load('key', cloneDeep(action.payload.schema));

  if (flow) {
    flow.setEntityCallback((type, uuid) => {
      switch (type) {
        case 'endpoint':
          return { ...entityRepo.endpoints[uuid] };
        case 'mapping':
          return { ...entityRepo.mappings[uuid] };
        case 'schema':
          return { ...entityRepo.models[uuid] };
        case 'flow':
          return { ...entityRepo.flows[uuid] };
        default:
          throw new Error(`${type} with uuid ${uuid} not found in flow.entityCallback `);
      }
    });

    /** TMP code to resolve the breaking change of editor settings */
    flow.filterFlow(flow, comp => {
      if (!comp.config.editor && comp.config.point.x && comp.config.point.y) {
        comp.config.editor = {
          location: { x: comp.config.point.x, y: comp.config.point.y }
        };
        delete comp.config.x;
        delete comp.config.y;
      }

      return undefined;
    });

    return {
      ...state,
      uuid: action.payload.uuid,
      flowSchema: action.payload.schema,
      flow,
      flowSummary: flow.validate(flow),
      structureHash: v4(),
      selectedComponent: null,
      activePanel: null,
      locked: action.payload.locked
    };
  }

  return state;
};
